import { z } from "zod";
import {
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

// --- Schemas ---

export const createClientSpaceBodySchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
});

export const updateClientSpaceBodySchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
});

export const addMemberBodySchema = z.object({
  user_id: z.string().min(1),
  role: z.enum(["manager", "member"]).default("member"),
});

// --- Path builders ---

export function clientSpacesPath(workspaceId: string): string {
  return `/api/workspaces/${workspaceId}/client-spaces`;
}

export function clientSpacePath(spaceId: string): string {
  return `/api/client-spaces/${spaceId}`;
}

export function clientSpaceMembersPath(spaceId: string): string {
  return `/api/client-spaces/${spaceId}/members`;
}

export function clientSpaceMemberPath(spaceId: string, userId: string): string {
  return `/api/client-spaces/${spaceId}/members/${userId}`;
}

export function clientSpaceInviteLinksPath(spaceId: string): string {
  return `/api/client-spaces/${spaceId}/invite-links`;
}

export function clientSpaceInviteLinkPath(
  spaceId: string,
  linkId: string,
): string {
  return `/api/client-spaces/${spaceId}/invite-links/${linkId}`;
}

export function clientSpaceWorkspaceLinksPath(spaceId: string): string {
  return `/api/client-spaces/${spaceId}/workspace-links`;
}

export function clientSpaceWorkspaceLinkPath(
  spaceId: string,
  linkId: string,
): string {
  return `/api/client-spaces/${spaceId}/workspace-links/${linkId}`;
}

// --- Response schemas ---

export const workspaceLinkSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  linked_by: z.string().nullable(),
  role: z.enum(["host", "linked"]),
  created_at: z.string(),
  workspace_name: z.string(),
  workspace_slug: z.string().nullable().optional(),
  workspace_logo_url: z.string().nullable().optional(),
  linked_by_name: z.string().nullable().optional(),
});

export type WorkspaceLink = z.infer<typeof workspaceLinkSchema>;

export const workspaceLinksResponseSchema = z.object({
  links: z.array(workspaceLinkSchema),
});

// --- Request helpers ---

function buildAuthHeaders(accessToken?: string): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

export async function createClientSpaceRequest({
  apiBaseUrl,
  accessToken,
  workspaceId,
  name,
  description,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  workspaceId: string;
  name: string;
  description?: string;
  fetchImpl?: FetchLike;
}): Promise<{ id: string }> {
  const fetchFn = fetchImpl ?? fetch;
  const body = createClientSpaceBodySchema.parse({ name, description });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${clientSpacesPath(workspaceId)}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to create client space");
  }

  return response.json();
}

export async function addClientSpaceMemberRequest({
  apiBaseUrl,
  accessToken,
  spaceId,
  userId,
  role,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  spaceId: string;
  userId: string;
  role?: "manager" | "member";
  fetchImpl?: FetchLike;
}): Promise<{ ok: true }> {
  const fetchFn = fetchImpl ?? fetch;
  const body = addMemberBodySchema.parse({ user_id: userId, role });

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${clientSpaceMembersPath(spaceId)}`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to add member to space");
  }

  return response.json();
}

export async function removeClientSpaceMemberRequest({
  apiBaseUrl,
  accessToken,
  spaceId,
  userId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  spaceId: string;
  userId: string;
  fetchImpl?: FetchLike;
}): Promise<{ ok: true }> {
  const fetchFn = fetchImpl ?? fetch;

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${clientSpaceMemberPath(spaceId, userId)}`,
    {
      method: "DELETE",
      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to remove member");
  }

  return response.json();
}

export async function revokeInviteLinkRequest({
  apiBaseUrl,
  accessToken,
  spaceId,
  linkId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  spaceId: string;
  linkId: string;
  fetchImpl?: FetchLike;
}): Promise<{ ok: true }> {
  const fetchFn = fetchImpl ?? fetch;

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${clientSpaceInviteLinkPath(spaceId, linkId)}`,
    {
      method: "DELETE",
      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to revoke invite link");
  }

  return response.json();
}

export async function getWorkspaceLinksRequest({
  apiBaseUrl,
  accessToken,
  spaceId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  spaceId: string;
  fetchImpl?: FetchLike;
}): Promise<{ links: WorkspaceLink[] }> {
  const fetchFn = fetchImpl ?? fetch;

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${clientSpaceWorkspaceLinksPath(spaceId)}`,
    {
      method: "GET",
      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to get workspace links");
  }

  return response.json();
}

export async function unlinkWorkspaceRequest({
  apiBaseUrl,
  accessToken,
  spaceId,
  linkId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  spaceId: string;
  linkId: string;
  fetchImpl?: FetchLike;
}): Promise<{ ok: true }> {
  const fetchFn = fetchImpl ?? fetch;

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${clientSpaceWorkspaceLinkPath(spaceId, linkId)}`,
    {
      method: "DELETE",
      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to unlink workspace");
  }

  return response.json();
}

// --- Join space ---

export const joinSpaceResponseSchema = z.object({ ok: z.literal(true) });

export function joinSpacePath(spaceId: string): string {
  return `/api/client-spaces/${spaceId}/join`;
}

export async function joinSpaceRequest({
  apiBaseUrl,
  accessToken,
  spaceId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken?: string;
  spaceId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof joinSpaceResponseSchema>> {
  const base = normalizeApiBaseUrl(apiBaseUrl);
  const doFetch = fetchImpl ?? fetch;

  const response = await doFetch(`${base}${joinSpacePath(spaceId)}`, {
    method: "POST",
    headers: buildAuthHeaders(accessToken),
  });

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to join space");
  }

  return response.json();
}
